import { alpha } from "@mui/system";
import { darken } from "@mui/material/styles";

// Coeficiente de escurecimento do fundo do canvas no modo claro — no modo claro,
// background.default e background.paper do tema ficam quase idênticos (contraste ~1.14:1),
// então o card do equipamento mal se distingue da área do diagrama; no modo escuro os dois já
// têm contraste suficiente (~1.07:1 seria igual de baixo em número, mas a diferença de matiz
// entre eles é perceptível, então não mexemos lá). Valor validado por contraste real: ver spec
// cor-nos-equipamentos-diagrama-design.md
const CANVAS_BACKGROUND_DARKEN_LIGHT = 0.06;

// Opacidade do ponto de referência do canvas no modo claro — no modo claro, o divider padrão do
// MUI (12% de preto) sobre o fundo do canvas fica com contraste ~1.3:1, bem abaixo do que o
// modo escuro já alcança (~1.38:1) só por causa da matiz azulada do fundo escuro. Aumentar a
// opacidade aqui é o que de fato resolve — escurecer o fundo sozinho não muda esse contraste
// (ponto e fundo escurecem junto, na mesma proporção)
const CANVAS_DOT_OPACITY_LIGHT = 0.22;

// Fundo da área do canvas do diagrama (fora dos cards de equipamento) — mesmo valor usado no
// canvas principal e nos previews read-only (DiagramaEquipamentosPreview, TemplatePreview), pra
// não repetir o coeficiente em cada lugar
export const getDiagramCanvasBackground = (theme) =>
  theme?.palette?.mode === "light"
    ? darken(theme?.palette?.background?.default, CANVAS_BACKGROUND_DARKEN_LIGHT)
    : theme?.palette?.background?.default;

// Cor do ponto de referência (grade pontilhada) do canvas — mesmo valor usado nos três lugares
// que renderizam o <Background> do React Flow (canvas principal e os dois previews read-only)
export const getDiagramDotColor = (theme) =>
  theme?.palette?.mode === "light"
    ? alpha(theme?.palette?.text?.primary, CANVAS_DOT_OPACITY_LIGHT)
    : theme?.palette?.divider;

// Estilos do canvas principal e da paleta de equipamentos
const styles = (theme) => ({
  // Estrutura base que permite alternar entre canvas embutido e overlay
  canvasShell: {
    width: "100%",
    position: "relative",
  },
  // Overlay ampliado que ocupa viewport sem desmontar React Flow
  canvasShellExpanded: {
    position: "fixed",
    inset: 0,
    zIndex: theme?.zIndex?.modal,
    p: { xs: 1, sm: 1.5 },
    display: "flex",
    flexDirection: "column",
    gap: 1,
    backgroundColor: theme?.palette?.background?.default,
  },
  // Barra de ações do canvas: logo à esquerda, botão de reduzir à direita
  canvasActionBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: 36,
  },
  canvasActionButton: {
    color: theme?.palette?.primary?.main,
  },
  // Logo reduzida para caber na altura da barra de ações. A logo externa costuma ser clara
  // (pensada pra fundos escuros/coloridos, ex: sidebar), mas esta barra usa o background.default
  // do tema — no modo claro isso é um fundo bem claro, então some sem uma sombra reforçando o
  // contorno (drop-shadow segue a forma real do PNG, ao contrário de um box-shadow retangular)
  canvasActionLogo: {
    height: 34,
    width: "auto",
    display: "block",
    filter:
      theme?.palette?.mode === "light"
        ? `drop-shadow(0 1px 3px ${alpha(theme?.palette?.common?.black, 0.45)})`
        : "none",
  },
  // Container do canvas com altura fixa, obrigatória para o React Flow renderizar
  canvasWrapper: {
    height: 500,
    width: "100%",
    borderRadius: 2,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: getDiagramCanvasBackground(theme),
  },
  // Dimensões do canvas quando overlay ocupa viewport
  canvasWrapperExpanded: {
    height: "calc(100vh - 64px)",
    minHeight: 0,
    flex: 1,
  },
});

export default styles;
